import type { ExternalEndpointOption } from "../../../interfaces/External.interface";

/**
 * The /api/ext routes an API client calls as its role (LiftCloud's
 * ExternalRoleRoutes): bank status, one bank's status, the command catalog of
 * a bank, sending a command, and the live feed. In the order the checklist
 * lists them.
 */
export const LIVE_CONTROL_URIS: readonly string[] = [
  "/api/ext/status/",
  "/api/ext/status/:bank_id/",
  "/api/ext/commands/:bank_id/",
  "/api/ext/command/:serverID",
  "/api/ext/subscribe",
];

const REPORTS_PREFIX = "/api/ext/reports/";

/**
 * The report families in the order the External API guide lists them. A
 * family not named here follows them alphabetically: the catalog's own order
 * is by random UUID, so it would differ from one install to the next.
 */
const REPORT_FAMILIES = ["faults", "traffic", "usage", "service", "all-banks", "frequency"];

/** Routes of one group by uri, since the catalog's own order differs per install. */
function byUri(endpoints: ExternalEndpointOption[]): ExternalEndpointOption[] {
  return [...endpoints].sort((a, b) => a.uri.localeCompare(b.uri));
}

function familyRank(family: string): number {
  const rank = REPORT_FAMILIES.indexOf(family);
  return rank === -1 ? REPORT_FAMILIES.length : rank;
}

/** A heading of the endpoint checklist and the endpoints under it. */
export interface EndpointGroup {
  /** "live", "reports:<segment>" (e.g. "reports:faults") or "other". */
  key: string;
  endpoints: ExternalEndpointOption[];
}

/** The report group of a /api/ext/reports/<group>/... route, or "" for any other route. */
function reportGroup(uri: string): string {
  if (!uri.startsWith(REPORTS_PREFIX)) return "";
  return uri.slice(REPORTS_PREFIX.length).split("/")[0] ?? "";
}

/**
 * The active catalog entries grouped as the checklist shows them: the live
 * routes first, in LIVE_CONTROL_URIS order, then one group per report family
 * (REPORT_FAMILIES order), then anything else. An
 * inactive entry is left out: the grant gate ignores it, so granting it would
 * change nothing.
 */
export function groupEndpoints(endpoints: ExternalEndpointOption[]): EndpointGroup[] {
  const active = endpoints.filter((endpoint) => endpoint.active);

  const live = LIVE_CONTROL_URIS.flatMap((uri) =>
    active.filter((endpoint) => endpoint.uri === uri),
  );

  const reports = new Map<string, ExternalEndpointOption[]>();
  const other: ExternalEndpointOption[] = [];
  active
    .filter((endpoint) => !LIVE_CONTROL_URIS.includes(endpoint.uri))
    .forEach((endpoint) => {
      const group = reportGroup(endpoint.uri);
      if (group === "") {
        other.push(endpoint);
        return;
      }
      reports.set(group, [...(reports.get(group) ?? []), endpoint]);
    });

  return [
    ...(live.length > 0 ? [{ key: "live", endpoints: live }] : []),
    ...Array.from(reports)
      .sort(([a], [b]) => familyRank(a) - familyRank(b) || a.localeCompare(b))
      .map(([group, list]) => ({ key: `reports:${group}`, endpoints: byUri(list) })),
    ...(other.length > 0 ? [{ key: "other", endpoints: byUri(other) }] : []),
  ];
}

/**
 * The ids the "Live control" preset checks: every active catalog entry for a
 * live route. The preset adds them to what is checked already and unchecks
 * nothing.
 */
export function liveControlIds(endpoints: ExternalEndpointOption[]): string[] {
  return endpoints
    .filter((endpoint) => endpoint.active && LIVE_CONTROL_URIS.includes(endpoint.uri))
    .map((endpoint) => endpoint.id);
}

/** True when any of ids is a live route, which LiftCloud refuses to a client with no role. */
export function includesLiveRoute(ids: string[], endpoints: ExternalEndpointOption[]): boolean {
  const live = new Set(liveControlIds(endpoints));
  return ids.some((id) => live.has(id));
}

/** The body fields of POST and PUT /api/externals that change grants. */
export interface GrantChanges {
  permissions: string[];
  permissions2delete: string[];
}

/**
 * What to send to grant `desired` and revoke nothing: the safe save when an
 * API client's current grants are not known, since it cannot take away a
 * grant the client holds without the page knowing it.
 */
export function grantAdditions(desired: string[]): GrantChanges {
  return { permissions: Array.from(new Set(desired)), permissions2delete: [] };
}

/**
 * What to send so an API client ends up holding exactly `desired`.
 *
 * With its `current` grants known, only the difference goes out: the ids
 * newly checked in `permissions` and the ids unchecked in
 * `permissions2delete`.
 *
 * LiftCloud does not report an API client's grants today, so `current` is
 * usually null. Then every desired id is sent in `permissions` and every
 * other catalog id (inactive ones included) in `permissions2delete`, which
 * leaves exactly `desired` whatever the client held: LiftCloud inserts a
 * grant the client already holds as a no-op (ON CONFLICT DO NOTHING), and
 * deleting a grant it does not hold deletes nothing. It also revokes grants
 * the administrator cannot see, so the page asks for it explicitly and uses
 * grantAdditions otherwise. `catalogIds` must be the whole, current catalog.
 */
export function grantChanges(
  current: string[] | null,
  desired: string[],
  catalogIds: string[],
): GrantChanges {
  const want = new Set(desired);
  if (current === null) {
    return {
      permissions: Array.from(want),
      permissions2delete: Array.from(new Set(catalogIds)).filter((id) => !want.has(id)),
    };
  }
  const held = new Set(current);
  return {
    permissions: Array.from(want).filter((id) => !held.has(id)),
    permissions2delete: Array.from(held).filter((id) => !want.has(id)),
  };
}
